import { Card as AntCard, Space } from 'antd';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ProjectId } from 'api/models';
import { Button } from 'components/atoms/button';
import { TextArea } from 'components/atoms/input';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { Option } from 'components/atoms/select/types';
import { ProjectSelect } from 'components/molecules/new-task-modal/project-dropdown';
import { OnCreate } from 'components/organisms/kanban-board/coulmn-context';
import { SingleLineEllipsis } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { useProjectsQuery } from 'queries';
import { isChildNode } from 'utils/is-child-node';

export const TaskName = styled(TextArea)`
  && {
    background: var(--white-100);
    resize: none;
    padding-right: 20%;
    min-height: 64px !important;
  }

  border: none;
`;

const Card = styled(AntCard)`
  min-height: 144px;

  .ant-card-body {
    padding: 0;
  }
`;

const ProjectSelectWrapper = styled.div`
  height: 40px;
  padding: var(--spacing-03);
  width: 80%;
  ${SingleLineEllipsis};
`;

const StyledSpace = styled(Space)`
  margin-left: var(--spacing-03);
  margin-bottom: var(--spacing-03);
`;

interface NewTaskCardProps {
  onCreate: OnCreate;
  onCancel: () => void;
  inputRef: React.Ref<HTMLInputElement>;
  currentProjectId: ProjectId | null;
}

export const NewTaskCardWithProject: FC<NewTaskCardProps> = ({
  onCreate,
  onCancel,
  inputRef,
  currentProjectId,
}) => {
  const taskNameLabel = useTranslation('task_name');
  const [state, setState] = useState('');
  const [selectedProject, setSelectedProject] =
    useState<Option | undefined | null>(null);

  const { data = [] } = useProjectsQuery();

  const projects = useMemo(
    () =>
      data.map((p) => ({
        label: p.projectName || '',
        value: p.projectId,
        imageUrl: p.customer.companyLogoUrl,
      })),
    [data],
  );

  useEffect(() => {
    if (projects && currentProjectId) {
      setSelectedProject(projects.find((p) => p.value === currentProjectId[1]));
    }
  }, [projects, currentProjectId]);
  const handleOnChange = (latest: Option | undefined | null) => {
    setSelectedProject(latest);
  };
  const handleSubmit = () => {
    if (selectedProject) {
      onCreate({
        taskName: state,
        projectId: Number(selectedProject.value),
      });
      setState('');
    }
  };
  const handleOutsideClick = () => {
    if (!state && !selectedProject) {
      onCancel();
    }
  };

  const onChange: React.ChangeEventHandler<HTMLTextAreaElement> = useCallback(
    (e) => {
      setState(e.target.value);
    },
    [],
  );

  const disableAdd = !selectedProject && !state;

  return (
    <OutsideClickHandler
      onOutsideClick={handleOutsideClick}
      allowed={isChildNode(['project-select'])}
    >
      <Card>
        <TaskName
          ref={inputRef}
          data-cy="modals.create-task.task-name"
          autoFocus
          value={state}
          onChange={onChange}
          placeholder={taskNameLabel}
          autoSize={{ minRows: 2, maxRows: 7 }}
        />
        <ProjectSelectWrapper>
          <ProjectSelect
            selected={selectedProject}
            options={projects}
            onChange={handleOnChange}
            disabled={Boolean(currentProjectId)}
          />
        </ProjectSelectWrapper>
        <StyledSpace size={0}>
          <Button
            type="primary"
            size="small"
            disabled={disableAdd}
            onClick={handleSubmit}
          >
            <FormattedMessage id="add" />
          </Button>
          <Button type="link" size="small" onClick={() => onCancel()}>
            <FormattedMessage id="cancel" />
          </Button>
        </StyledSpace>
      </Card>
    </OutsideClickHandler>
  );
};
